import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaAndroid,
  FaArrowLeft,
  FaArrowRight,
  FaBell,
  FaBrain,
  FaChartPie,
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaCode,
  FaCopy,
  FaFile,
  FaVideo,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";

import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";
import { useReminders } from "../context/RemindersContext";
import { formatCustomDate } from "../utils/formatCustomDate";
import { getCourseLabel } from "../utils/getCourseLabel";

// hardcoded 4 courses
const courses = [
  { id: 1, title: "AndroidDeveloper", subtitle: "fundamentals", icon: FaAndroid },
  { id: 2, title: "ArtificialIntelligence", subtitle: "fundamentals", icon: FaBrain },
  { id: 3, title: "DataAnalysis", subtitle: "fundamentals", icon: FaChartPie },
  { id: 4, title: "Programming", subtitle: "fundamentals", icon: FaCode },
];

const SupportSessions = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [loadingEventId, setLoadingEventId] = useState(null);
  const { fetchReminders } = useReminders();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");

  const tabs = [
    { id: 1, label: "LiveNow", type: "live" },
    { id: 2, label: "Upcoming", type: "upcoming" },
    { id: 3, label: "Summary", type: "summary" },
  ];
  // ✅ Map query param to tab id
  const getTabIdFromType = (t) => {
    const found = tabs.find((tab) => tab.type === t);
    return found ? found.id : 1; // default to 1 if invalid
  };

  const allCoursesOption = { id: null, title: "AllCourses", subtitle: "", icon: FaCode };
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [activeTab, setActiveTab] = useState(getTabIdFromType(type));
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState({});

  const handleCopyPath = (path, sessionId) => {
    const textarea = document.createElement("textarea");
    textarea.value = path;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");

      // set only for this session
      setCopySuccess((prev) => ({ ...prev, [sessionId]: true }));

      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [sessionId]: false }));
      }, 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/sessions`, {
          params: {
            type: tabs.find((t) => t.id === activeTab)?.type,
            ...(selectedCourseId ? { course_id: selectedCourseId } : {}), // only send course_id if selected
          },
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        });

        setSessions(response?.data?.data?.data || []);
      } catch (err) {
        if (err.response?.status === 401) {
          // Unauthorized: remove user and redirect to login
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else {
          setError("Failed to load events. Please try again later.");
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    };

    // ✅ Always fetch sessions, even for "All Courses"
    fetchSessions();
  }, [selectedCourseId, activeTab, i18n.language]);

  useEffect(() => {
    if (location.state?.tab) setActiveTab(location.state.tab);
  }, [location.state]);

  const handleTabChange = (id) => {
    setActiveTab(id);
    const tabType = tabs.find((t) => t.id === id)?.type;
    navigate(`/support-sessions?type=${tabType}`, { replace: true });
  };

  const handleJoinSession = (session) => {
    navigate(
      `/meeting?link=${encodeURIComponent(session.link)}&meetingID=${session.meeting_id}&sessionID=${session.id}&type=1`
    );
  };

  const handleSetReminder = async (sessionId) => {
    setLoadingEventId(sessionId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reminder`,
        {
          type: 2, // type 2 = sessions
          model_id: sessionId,
        },
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      const reminderId = response?.data?.data?.id; // get reminder ID from backend

      // ✅ Update UI immediately
      setSessions((prevSessions) =>
        prevSessions.map((s) => (s.id === sessionId ? { ...s, is_reminder: reminderId } : s))
      );
      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to set reminder. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingEventId(null);
    }
  };

  const handleRemoveReminder = async (reminderId, sessionId) => {
    setLoadingEventId(sessionId);

    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/reminder/${reminderId}`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });

      // ✅ Update UI immediately
      setSessions((prevSessions) =>
        prevSessions.map((s) => (s.is_reminder === reminderId ? { ...s, is_reminder: null } : s))
      );
      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to remove reminder. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingEventId(null);
    }
  };

  const renderLiveNow = () => {
    if (!sessions || sessions.length === 0) {
      return <p className="text-center text-gray-500">{t("Nosessionrightnow")}</p>;
    }

    return (
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
        {sessions.map((session, idx) => (
          <div
            key={session.id ?? idx}
            className="flex flex-col overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-gray-200"
          >
            {/* Info section */}
            <div className="flex flex-col gap-2 bg-gradient-to-br from-purple-500/90 to-purple-600/90 p-4 text-white">
              {/* Badge */}
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-semibold uppercase">
                  {t("LiveNow")}
                </span>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex w-full flex-col overflow-hidden sm:flex-row sm:items-center sm:gap-3">
                  <div className="flex items-center gap-2">
                    <p className="flex-1 text-sm font-bold text-white sm:text-base">
                      {getCourseLabel(session?.course_id, i18n.language)}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-sm">{session?.description}</p>
              <div className="flex flex-col">
                <div className="flex-wrap items-center gap-x-2 gap-y-1">
                  {session?.coaches?.map((item, index) => (
                    <span key={index} className="whitespace-nowrap text-xs italic text-purple-100 sm:text-sm">
                      {index > 0 && <span className="mx-1 text-purple-400">/</span>}
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-2 p-3 sm:gap-3 sm:p-4">
              <button
                onClick={() => handleJoinSession(session)}
                className="flex items-center gap-2 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 px-4 py-2 text-xs font-medium text-white shadow-lg transition sm:px-5 sm:text-sm"
              >
                <FaVideo /> {t("JoinSession")}
              </button>

              <button
                onClick={() =>
                  handleCopyPath(
                    `${process.env.REACT_APP_BASE_URL}meeting?link=${encodeURIComponent(
                      session.link
                    )}&meetingID=${session.meeting_id}&sessionID=${session.id}&type=1`,
                    session.id
                  )
                }
                className="flex items-center gap-2 rounded-full border border-purple-500 px-4 py-2 text-xs font-medium text-purple-500 shadow-lg transition hover:bg-purple-50 hover:text-purple-700 sm:px-5 sm:text-sm"
              >
                <FaCopy /> {copySuccess[session.id] ? t("Copied") : t("CopyLink")}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUpcoming = () => {
    if (!sessions || sessions.length === 0) {
      return <p className="text-center text-gray-500">{t("Nosessionrightnow")}</p>;
    }

    return (
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
        {sessions?.map((session) => (
          <div
            key={session?.id}
            className="flex flex-col overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-gray-200"
          >
            <div className="flex flex-col gap-2 bg-gradient-to-br from-purple-500/90 to-purple-600/90 p-4 text-white">
              {/* Badge */}
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-yellow-400 px-3 py-1 text-xs font-semibold uppercase">
                  {t("Upcoming")}
                </span>
              </div>

              {/* Course + Coach names */}
              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex w-full flex-col overflow-hidden sm:flex-row sm:items-center sm:gap-3">
                    <div className="flex items-center gap-2">
                      <p className="flex-1 text-sm font-bold text-white sm:text-base">
                        {getCourseLabel(session?.course_id, i18n.language)}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Description */}
                <div className="flex flex-col gap-1">
                  <p className="text-xs sm:text-sm">{session?.description}</p>
                </div>
              </div>

              <div>
                {/* Date */}
                <div className="flex items-center gap-1 text-sm text-white/60">
                  {/* Date with clock icon */}
                  <div className="flex items-center gap-1 py-1 font-medium">
                    <FaClock />
                    <span>{formatCustomDate(session?.start_date)}</span>
                  </div>

                  {/* Time range */}
                  <div className="px-2 py-1">
                    {session?.start_time} - {session?.end_time}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {session?.coaches?.map((item, index) => (
                    <span key={index} className="whitespace-nowrap text-xs italic text-purple-100 sm:text-sm">
                      {index > 0 && <span className="mx-1 text-purple-400">/</span>}
                      {item}
                    </span>
                  ))}
                </div>
                <p className="text-xs">*{t("upcomingSessionsAlert")}</p>
              </div>
            </div>
            {/* Buttons */}
            <div className="flex flex-wrap gap-2 p-3 sm:gap-3 sm:p-4">
              <button
                onClick={
                  session.is_reminder
                    ? () => handleRemoveReminder(session.is_reminder, session.id)
                    : () => handleSetReminder(session.id)
                }
                className={`flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-medium shadow-lg transition-all duration-300 sm:w-auto sm:px-5 sm:text-sm ${
                  session.is_reminder
                    ? "border border-purple-600 bg-white text-purple-600 hover:bg-purple-50 hover:text-purple-800"
                    : "bg-gradient-to-br from-purple-500 to-purple-600 text-white hover:from-purple-500 hover:to-purple-600"
                } ${loadingEventId === session.id ? "cursor-not-allowed opacity-50" : ""}`}
              >
                {loadingEventId === session.id ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-purple-600"></div>
                ) : (
                  <FaBell
                    size={14}
                    className={`transition-colors duration-300 ${
                      session.is_reminder ? "text-purple-600" : "text-white"
                    }`}
                  />
                )}
                {session.is_reminder ? t("RemoveReminder") : t("SetReminder")}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSummary = () => (
    <div className="space-y-4">
      {sessions?.map((session) => (
        <div
          key={session?.id}
          className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200"
        >
          <div>
            <h2 className="font-semibold text-gray-800">
              {getCourseLabel(session?.course_id, i18n.language)}
            </h2>
            {/* Description */}
            <div className="mb-2 flex flex-col">
              <p className="line-clamp-2 text-sm text-gray-600">{session?.description}</p>
            </div>
            {/* Date */}
            <div className="flex items-center gap-1 text-sm">
              {/* Date with clock icon */}
              <div className="flex items-center gap-1 py-1 font-medium">
                <FaClock />
                <span>{formatCustomDate(session?.start_date)}</span>
              </div>

              {/* Time range */}
              <div className="px-2 py-1">
                {session?.start_time} - {session?.end_time}
              </div>
            </div>
            {session?.coaches?.map((item, index) => (
              <span key={index} className="text-xs italic text-purple-700 sm:text-sm">
                {index > 0 && <span className="mx-1 text-purple-800">/</span>}
                {item}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {session.video && <DownloadButton url={session.video} name={session.name} />}
            {session.document && <DownloadButton url={session.document} name={session.name} />}
          </div>
        </div>
      ))}
    </div>
  );

  const DownloadButton = ({ url, name }) => {
    const handleDownload = async () => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();

        // Extract extension safely
        const extension = url.split(".").pop().split(/\#|\?/)[0];
        const fileName = `${(name || "file").replace(/\s+/g, "_")}.${extension}`;

        // Create a temporary link
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        console.error("Download failed", err);
      }
    };

    // Pick icon based on file type
    const extension = url.split(".").pop().split(/\#|\?/)[0].toLowerCase();
    const isVideo = ["mp4", "mov", "avi", "mkv", "webm"].includes(extension);

    return (
      <button onClick={handleDownload} className="transition hover:text-indigo-300">
        {isVideo ? <FaVideo /> : <FaFile />}
      </button>
    );
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 pt-0">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-purple-500 hover:text-purple-500"
        >
          {i18n.language == "ar" ? (
            <FaChevronCircleRight size={18} className="text-purple-500" />
          ) : (
            <FaChevronCircleLeft size={18} className="text-purple-500" />
          )}
          {t("back")}
        </button>
        <div className="hidden justify-end lg:flex">{/* <LanguageSwitcher defaultTheme /> */}</div>
      </div>

      {/* Course selection row - Flexbox with mobile tabs */}
      <div className="flex flex-wrap gap-2">
        {[allCoursesOption, ...courses].map((course) => {
          const isSelected = selectedCourseId === course.id;

          return (
            <div
              key={course.id ?? "all"}
              className="transition-all duration-300"
              style={{
                flex: isSelected ? "3 1 0%" : "1 1 0%",
              }}
            >
              <div
                onClick={() => setSelectedCourseId(course.id)}
                className={`flex cursor-pointer flex-col items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md transition-all duration-300 ${
                  isSelected ? "ring-2 ring-purple-400" : ""
                } h-10 text-sm font-medium sm:h-32 sm:w-full sm:px-4 sm:py-2 sm:text-base`}
              >
                {/* If it's "All Courses" just show text */}
                {course.id === null ? (
                  <span className="text-sm font-semibold">{t("AllCourses")}</span>
                ) : (
                  <>
                    <div className="flex items-center gap-1 sm:hidden">
                      <course.icon size={16} />
                      {isSelected && <h3 className="text-[10px] font-semibold">{course.title}</h3>}
                    </div>
                    <div className="hidden h-full w-full sm:flex sm:flex-col sm:items-start sm:justify-between">
                      <div className="text-2xl">
                        <course.icon />
                      </div>

                      {i18n.language == "ar" ? (
                        <div>
                          {course.subtitle && <p className="text-sm opacity-80">{t(course.subtitle)}</p>}
                          <h3 className="text-sm font-semibold">{t(course.title)}</h3>
                        </div>
                      ) : (
                        <div>
                          <h3 className="text-sm font-semibold">{t(course.title)}</h3>
                          {course.subtitle && <p className="text-sm opacity-80">{t(course.subtitle)}</p>}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sessions section (now always works even for "All Courses") */}
      <>
        {/* Tabs */}
        <div className="flex justify-center gap-3">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${
                activeTab === id
                  ? "bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md"
                  : "bg-gray-100 text-purple-600 hover:bg-purple-50"
              }`}
            >
              {t(label)}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
          </div>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : (
          <>
            {activeTab === 1 && renderLiveNow()}
            {activeTab === 2 && renderUpcoming()}
            {activeTab === 3 && renderSummary()}
          </>
        )}
      </>
    </div>
  );
};

export default SupportSessions;
